import { Theme } from '@/types/search';
import { CSSProperties } from 'react';
import { readableColor, getLuminance, getContrast } from 'polished';
import get from './get';

const getIconColor = (bgColor: string = '#000', fgColor: string = '#FFF') => {
  const contrast = getContrast(bgColor, fgColor);

  // if the contrast is good enough, return the fgColor. Otherwise, return a readable color
  if (contrast > 4.14) return fgColor;

  const isFGLight = getLuminance(fgColor) > 0.5;
  const defaultLightBgForeground = isFGLight ? '#000' : fgColor;
  const defaultDarkBgForeground = isFGLight ? fgColor : '#FFF';

  return readableColor(
    bgColor,
    defaultLightBgForeground,
    defaultDarkBgForeground,
    false,
  );
};

export const injectCustomFont = (fontSrc: string, fontFamily: string) => {
  const existingFont = document.querySelector<HTMLLinkElement>('link#bot-font');
  const existingFontFace = document.querySelector<HTMLStyleElement>(
    'style#bot-font-face',
  );

  const fragment = document.createDocumentFragment();
  // check if it is a link to font css, i.e. not an actual font file ().woff2, .woff, .ttf)
  if (!fontSrc.match(/\.woff2$|\.woff$|\.ttf$/)) {
    if (existingFont) {
      existingFont.href = fontSrc;
    } else {
      const fontLink = document.createElement('link');
      fontLink.href = fontSrc;
      fontLink.rel = 'stylesheet';
      fontLink.id = 'bot-font';
      fragment.appendChild(fontLink);
    }
  } else {
    // get the font format from the fontSrc
    const fontFormat = fontSrc.split('.').pop() as 'woff2' | 'woff' | 'ttf';
    // map font format to the correct format string
    const fontFormats = {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    };

    // create the font face string
    const innerHTML = `
      @font-face {
        font-family: '${fontFamily}';
        font-display: swap;
        src: url('${fontSrc}') format('${fontFormats[fontFormat]}');
      }
    `;

    // if it is a font file, inject the font face
    if (existingFontFace) {
      existingFontFace.innerHTML = innerHTML;
    } else {
      const style = document.createElement('style');
      style.id = 'bot-font-face';
      style.innerHTML = innerHTML;
      fragment.appendChild(style);
    }
  }

  if (fragment.children.length) {
    document.head.appendChild(fragment);
  }
};

const injectDefaultFont = () => {
  const existingFont = document.getElementById('bot-font');
  if (existingFont?.getAttribute('href')?.includes('satoshi')) return;
  const font = document.createElement('link');
  font.href = `https://api.fontshare.com/v2/css?f[]=satoshi@1,700,500,301,300,400&display=swap`;
  font.rel = 'stylesheet';
  font.id = 'bot-font';
  document.head.appendChild(font);
};

export const injectBrainfishStyles = (theme: Theme, styles?: CSSProperties) => {
  const cssVariableNames: Record<string, string> = {
    fontFamily: '--bfsearch-container-font-family',
    inputColor: '--bfsearch-input-color',
    inputBorderColor: '--bfsearch-input-border-color',
    inputBgColor: '--bfsearch-input-bg-color',
    inputBorderWidth: '--bfsearch-input-border-width',
    inputButtonColor: '--bfsearch-input-button-color',
    inputButtonBgColor: '--bfsearch-input-button-bg-color',
    textBaseColor: '--bfsearch-text-base-color',
    categoryTitleColor: '--bfsearch-category-title-color',
    categoryTextColor: '--bfsearch-category-text-color',
    borderColor: '--bfsearch-border-color',
    fontWeight: '--bfsearch-font-weight',
    iconColor: '--bfsearch-icon-color',
    highlightColor: '--bfsearch-highlight-color',
  };

  type CssVariableNames = typeof cssVariableNames;
  type CssVariables = Partial<Record<keyof CssVariableNames, string>>;

  const cssVariables: CssVariables = {};
  if (theme) {
    if (theme.fontSrc && theme.fontFamily) {
      injectCustomFont(theme.fontSrc, theme.fontFamily);
    } else {
      injectDefaultFont();
    }
    for (const [key, value] of Object.entries(theme)) {
      if (key in cssVariableNames) {
        const cssVariableName = cssVariableNames[key];
        cssVariables[cssVariableName] = value.toString();
      }
    }

    // set the input button bg color if it exists or default to black
    const inputButtonBgColor = get(theme, 'inputButtonBgColor', '#000');
    const categoryTextColor = get(theme, 'suggestedQuestionsTextColor', '#000');
    const categoryTitleColor = get(
      theme,
      'suggestedQuestionsTextColor',
      '#000',
    );

    cssVariables['--bfsearch-input-button-bg-color'] = inputButtonBgColor;

    const inputButtonColor = get(theme, 'inputButtonColor', '#FFF');

    // Use readableColor function to set the input button color
    cssVariables['--bfsearch-input-button-color'] = getIconColor(
      inputButtonBgColor,
      inputButtonColor,
    );

    cssVariables['--bfsearch-category-text-color'] = categoryTextColor;
    cssVariables['--bfsearch-category-title-color'] = categoryTitleColor;
  }

  // if styles exists, convert it to css
  let stylesCss = '';
  if (styles) {
    stylesCss = Object.entries(styles)
      .map(
        ([key, value]) =>
          `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`,
      )
      .join(' ');
  }

  const hostCss = `
  :host {
    display: block;
    height: 100%;
    overflow-y: visible;
    ${stylesCss}

    ${Object.entries(cssVariableNames)
      .filter(([_key, value]) => value in cssVariables)
      .map(([_key, value]) => {
        const cssVariableValue = cssVariables[value];

        return `${value}: ${cssVariableValue};`;
      })
      .join('\n')}
  }
`;

  return hostCss;
};
